import React, { useRef, useState, useEffect, TouchEvent, useCallback } from 'react';
import { RegularButton } from 'fronton-react';
import { ShortArrow } from 'assets';
import { useBreakpoints, useIsomorphicLayoutEffect } from 'common/hooks';
import { TProjectConfiguration } from 'pages/common/types';
import { CardWidth, ScrollDirection, CALCULATORS_LAMINATE_OPEN_CONFIGURATION_EVENT } from '../../constants';
import { ProjectCard } from '../project-card';
import {
  carouselButton,
  carouselButtonLeft,
  carouselButtonRight,
  carouselContainer,
  carouselTrack,
  iconLeft,
  iconStyles,
  projectCardWrapper,
  verticalTrack,
} from './styles';

type TProjectCarouselProps = {
  configurations: TProjectConfiguration[];
  onProjectClick?: (project: TProjectConfiguration) => void;
  destroySlider?: boolean;
};

export const SavedProjectsCarousel: React.FC<TProjectCarouselProps> = ({
  configurations,
  onProjectClick,
  destroySlider = false,
}) => {
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [cardWidth, setCardWidth] = useState<CardWidth>(CardWidth.DESKTOP);

  const carouselRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const breakpoints = useBreakpoints();

  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  useIsomorphicLayoutEffect(() => {
    const updateCardWidth = (): void => {
      if (breakpoints) {
        if (breakpoints.mobile || breakpoints.sMobile) {
          setCardWidth(CardWidth.MOBILE);
        } else if (breakpoints.tablet) {
          setCardWidth(CardWidth.LAPTOP);
        } else {
          setCardWidth(CardWidth.DESKTOP);
        }
      } else {
        setCardWidth(CardWidth.DESKTOP);
      }

      if (carouselRef.current) {
        carouselRef.current.scrollLeft = 0;
      }
    };

    updateCardWidth();
  }, [breakpoints]);

  const isDesktop = cardWidth === CardWidth.DESKTOP;
  const isSliderEnabled = isDesktop && !destroySlider;

  const checkArrows = useCallback((): void => {
    if (!carouselRef.current) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;

    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    const carousel = carouselRef.current;

    if (carousel && isSliderEnabled) {
      carousel.addEventListener('scroll', checkArrows);
      checkArrows();

      return () => carousel.removeEventListener('scroll', checkArrows);
    }

    // Фикс ошибки eslint
    return undefined;
  }, [isSliderEnabled, checkArrows]);

  useEffect(() => {
    if (isSliderEnabled) {
      checkArrows();
    }
  }, [configurations, checkArrows, cardWidth, isSliderEnabled]);

  const scrollOneCard = (direction: ScrollDirection): void => {
    if (!carouselRef.current) return;

    const scrollAmount = direction === ScrollDirection.LEFT ? -cardWidth : cardWidth;

    carouselRef.current.scrollBy({
      left: scrollAmount,
      behavior: 'smooth',
    });
  };

  const scrollThreeCards = (direction: ScrollDirection): void => {
    if (!carouselRef.current) return;

    const scrollAmount = direction === ScrollDirection.LEFT ? -cardWidth * 3 : cardWidth * 3;

    carouselRef.current.scrollBy({
      left: scrollAmount,
      behavior: 'smooth',
    });
  };

  const handleOpenProject = (configuration: TProjectConfiguration) => {
    if (onProjectClick) {
      onProjectClick(configuration);

      return;
    }

    document.body.dispatchEvent(
      new CustomEvent(CALCULATORS_LAMINATE_OPEN_CONFIGURATION_EVENT, { detail: configuration })
    );
  };

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>): void => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>): void => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (): void => {
    if (touchStartX.current && touchEndX.current) {
      const diff = touchStartX.current - touchEndX.current;
      const threshold = 50;

      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          scrollOneCard(ScrollDirection.RIGHT);
        } else {
          scrollOneCard(ScrollDirection.LEFT);
        }
      }

      touchStartX.current = 0;
      touchEndX.current = 0;
    }
  };

  const trackClassName = destroySlider ? `${carouselTrack} ${verticalTrack}` : carouselTrack;
  const cursorStyle = isDesktop || destroySlider ? 'default' : 'grab';
  const showDesktopControls = isDesktop && destroySlider === false;
  const touchHandlers = destroySlider
    ? {}
    : {
        onTouchStart: handleTouchStart,
        onTouchMove: handleTouchMove,
        onTouchEnd: handleTouchEnd,
      };

  return (
    <div className={carouselContainer}>
      {showDesktopControls && showLeftArrow && (
        <RegularButton
          onClick={() => scrollThreeCards(ScrollDirection.LEFT)}
          className={`${carouselButton} ${carouselButtonLeft}`}
          label="Прокрутить влево"
          iconOnly
          invert
          rounded
          size="m">
          <ShortArrow className={`${iconStyles} ${iconLeft}`} direction={ScrollDirection.LEFT} />
        </RegularButton>
      )}

      <div
        ref={carouselRef}
        className={trackClassName}
        data-testid="projects-carousel-track"
        style={{ cursor: cursorStyle }}
        {...touchHandlers}>
        {configurations.map((project, index) => (
          <div
            key={project.id}
            ref={index === 0 ? cardRef : null}
            onClick={() => handleOpenProject(project)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                handleOpenProject(project);
              }
            }}
            className={projectCardWrapper}
            role="button"
            tabIndex={0}>
            <ProjectCard project={project} cardWidth={cardWidth} fullWidth={destroySlider} />
          </div>
        ))}
      </div>

      {showDesktopControls && showRightArrow && (
        <RegularButton
          onClick={() => scrollThreeCards(ScrollDirection.RIGHT)}
          className={`${carouselButton} ${carouselButtonRight}`}
          label="Прокрутить вправо"
          iconOnly
          invert
          rounded
          size="m">
          <ShortArrow className={iconStyles} direction={ScrollDirection.RIGHT} />
        </RegularButton>
      )}
    </div>
  );
};
